// The password and string lengths live in @faceup/validation, next to the schemas built from them
export {
  LONG_STRING_MAX_LENGTH,
  MEDIUM_STRING_MAX_LENGTH,
  SHORT_STRING_MAX_LENGTH,
  SHORTEST_STRING_MAX_LENGTH,
  STRING_MIN_LENGTH,
  TEXT_MAX_LENGTH,
  USER_PASSWORD_MIN_LENGTH,
} from '@faceup/validation'

export const INSTITUTION_PASSWORD_MIN_LENGTH = 5
export const INSTITUTION_PASSWORD_MAX_LENGTH = 30
// institution password must contain letters and digits only (no whitespace and no diacritics)
export const INSTITUTION_PASSWORD_VALIDATION = /^[a-zA-Z0-9]{5,30}$/
export const OPAQUE_SHORT_ID_VALIDATION = /^[a-z0-9]{5,30}$/
// a DNS host without scheme, port, path or credentials, so it can be interpolated into a URL as
// its host; the last label needs a letter, which keeps IPv4 literals out
export const SUBDOMAIN_VALIDATION =
  /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)*(?=[a-z0-9-]*[a-z])[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i

// regex for user name; it allows all lower/upper/diacritics and `-'. `; for 2 groups - each 2-50 chars
export const USER_NAME_REGEX = /^[a-zA-ZÀ-ž\-'. ]{2,50}(?: [a-zA-ZÀ-ž\-'. ]{2,50})+$/

export const ATTACHMENT_MAX_FILES_COUNT = 20
export const ATTACHMENT_MAX_FILE_SIZE_BYTES = 52_428_800 // 50 * 2^20 B ~ 50 MB
export const CUSTOM_DOCUMENTS_QUOTA_BYTES = 1_073_741_824 // 2^30 B ~ 1 GB

export const VOICE_MESSAGE_MAX_COUNT = 3
export const VOICE_MESSAGE_MAX_DURATION_MS = 15 * 60 * 1000 // 15 minutes

// Transport-layer guard for graphql-upload against multipart bombs, not a product limit:
// the real caps are enforced per answer (ATTACHMENT_MAX_FILES_COUNT files /
// VOICE_MESSAGE_MAX_COUNT voice messages per answer). Forms have no item-count limit, so
// this is a deliberately generous bound that clears any realistic form while still capping
// abuse; bump it if a legitimately large form ever hits it.
export const MAX_UPLOAD_FILES_PER_REQUEST = 100

export const MAX_SELECT_FORM_ITEM_OPTIONS_COUNT = 400

export const MAX_STAR_COUNT = 10

export const MIN_SCALE_VALUE = 0
export const MAX_SCALE_VALUE = 10

type MimetypeType = 'application' | 'audio' | 'image' | 'text' | 'video'

export const ALLOWED_IMAGE_MIME_TYPE_EXTENSION_MAP = {
  'image/jpeg': ['jpeg', 'jpg'],
  'image/png': ['png'],
} as const satisfies Record<`image/${string}`, readonly string[]>

export const ALLOWED_FILE_MIME_TYPE_EXTENSION_MAP = {
  ...ALLOWED_IMAGE_MIME_TYPE_EXTENSION_MAP,
  'video/mp4': ['mp4'],
  'video/quicktime': ['mov'],
  'video/x-msvideo': ['avi'],
  'audio/mpeg': ['mp3'],
  'audio/x-wav': ['wav'],
  'audio/wav': ['wav'],
  'audio/ogg': ['oga'],
  'application/pdf': ['pdf'],
  'application/x-latex': ['latex'],
  'application/x-tex': ['tex'],
  'text/plain': ['c', 'cc', 'h', 'txt', 'log', 'json'],
  'application/msword': ['doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['docx'],
  'application/vnd.oasis.opendocument.text': ['odt'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['xlsx'],
  'application/vnd.ms-excel': ['xls'],
  'application/vnd.oasis.opendocument.spreadsheet': ['ods'],
} as const satisfies Record<`${MimetypeType}/${string}`, readonly string[]>
export type AllowedFileExtension =
  (typeof ALLOWED_FILE_MIME_TYPE_EXTENSION_MAP)[keyof typeof ALLOWED_FILE_MIME_TYPE_EXTENSION_MAP][number]

export const ALLOWED_IMPORT_FILE_MIME_TYPE_EXTENSION_MAP = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['xlsx'],
  'application/json': ['json'],
  'text/csv': ['csv'],
}

export const ALLOWED_FILE_MIME_TYPE_EXTENSION_PLUS_DEPRECATED_MAP = {
  ...ALLOWED_FILE_MIME_TYPE_EXTENSION_MAP,
  // see commit 5b3672e15fabe92ca3a52f2b2dd6b7752761aaf0
  'application/octet-stream': ['mp3'],
} as const satisfies Record<`${MimetypeType}/${string}`, readonly string[]>

export const MAX_API_KEYS_COUNT = 10

export const CASE_RATING_RANGE = [1, 2, 3, 4, 5] as const
export type CaseRating = (typeof CASE_RATING_RANGE)[number]

export const INVESTIGATION_EVIDENCE_NAME_MAX_LENGTH = 100
export const INVESTIGATION_EVIDENCE_DESCRIPTION_MAX_LENGTH = 500
